/**
 * Keeps endpoint-derived work state fresh. Entity records have no push channel, so a second
 * browser (or a rotation) is discovered by refetching on reconnect, on regaining focus after a
 * missed tick, and on a slow timer that only runs while this tab is visible and focused (hidden
 * tabs send nothing), and mirrored live claims are verified by the run's own daemon-sealed
 * snapshot (the materialized state; never a replay of its events).
 */
import { focusManager, type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { type Backend, useBackend } from './backend.js'
import type { ConnState } from './hub.js'
import { useConnection } from './queries.js'
import { isActiveTab } from './query-client.js'
import type { RunSummary } from './types.js'
import { ENTITY_SOURCE } from './work-projection.js'

export const WORK_REFRESH_MS = 60_000
const WORK_KEYS = [['runs'], ['projects'], ['workstreams'], ['workstream'], ['tasks'], ['task']]

export function refreshWork(qc: Pick<QueryClient, 'invalidateQueries'>) {
  for (const queryKey of WORK_KEYS) void qc.invalidateQueries({ queryKey })
}

/**
 * Seed mirrored, still-live runs from their authenticated snapshots so our own copy of the
 * run state can confirm them (daemon pushes keep it current; no event subscription).
 */
export function watchMirroredRuns(
  b: Pick<Backend, 'transcriptStore' | 'artifactAccess'>,
  runs: RunSummary[],
) {
  for (const r of runs)
    if (
      r.source === ENTITY_SOURCE &&
      (r.status === 'running' || r.status === 'waiting' || r.status === 'compacting')
    )
      void b.transcriptStore(r.runId, b.artifactAccess.accountId).ready()
  return () => {}
}

export function useWorkSync(runs: RunSummary[] | undefined) {
  const b = useBackend()
  const qc = useQueryClient()
  const conn = useConnection()
  const prev = useRef<ConnState>(conn)
  const missed = useRef(false)
  useEffect(() => {
    if (conn === 'live' && prev.current !== 'live') {
      // A hidden tab catches up when it is next looked at, not now.
      if (document.visibilityState === 'visible') refreshWork(qc)
      else missed.current = true
    }
    prev.current = conn
  }, [conn, qc])
  useEffect(() => {
    if (!b.content) return
    let last = Date.now()
    const refresh = () => {
      last = Date.now()
      missed.current = false
      refreshWork(qc)
    }
    const tick = () => {
      if (isActiveTab()) refresh()
      else missed.current = true
    }
    // Regaining focus refreshes once, and only if a tick (or a reconnect) was missed meanwhile.
    const off = focusManager.subscribe((focused) => {
      if (focused && (missed.current || Date.now() - last >= WORK_REFRESH_MS)) refresh()
    })
    const timer = setInterval(tick, WORK_REFRESH_MS)
    return () => {
      clearInterval(timer)
      off()
    }
  }, [b, qc])
  const key = (runs ?? [])
    .filter((r) => r.source === ENTITY_SOURCE)
    .map((r) => `${r.runId}:${r.status}`)
    .join(',')
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` summarizes the mirrored runs.
  useEffect(() => (b.content && runs ? watchMirroredRuns(b, runs) : undefined), [b, key])
}
